/**
 * Medicines mock adapter.
 *
 * Mirrors `medicinesApi` / `drugDbApi` in `../medicines.ts` so the
 * `MOCKS_ENABLED ? mock : real` swap is a no-op for callers. Seeds an
 * in-memory VN drug catalogue so `/settings/medicines` (list, categories,
 * routes, create/update, show/hide, drug-db import) is fully click-through
 * without a backend or tenant header.
 */
import { delay } from "./registry";
import type { PageResponse } from "../../types/patient";
import type {
  DrugDbItem,
  DrugRouteResponse,
  ImportFromDrugDbRequest,
  MedicineCategoryResponse,
  MedicineClassification,
  MedicineCreateRequest,
  MedicineListFilter,
  MedicineResponse,
  MedicineSummaryResponse,
  MedicineUpdateRequest,
} from "../../types/medicine";

/* ------------------------------------------------------------------ */
/* Seed data                                                          */
/* ------------------------------------------------------------------ */

const nowISO = new Date().toISOString();

const CATEGORIES: MedicineCategoryResponse[] = [
  ["cat-khang-sinh", "KS", "Kháng sinh", 1],
  ["cat-giam-dau", "GD", "Giảm đau – hạ sốt", 2],
  ["cat-tieu-hoa", "TH", "Tiêu hoá", 3],
  ["cat-ho-hap", "HH", "Hô hấp", 4],
  ["cat-tim-mach", "TM", "Tim mạch", 5],
  ["cat-vitamin", "VTM", "Vitamin – khoáng chất", 6],
].map(([id, code, name, sortOrder]) => ({
  id: id as string,
  code: code as string,
  name: name as string,
  sortOrder: sortOrder as number,
  isSystem: true,
  createdAt: nowISO,
  updatedAt: nowISO,
}));

const ROUTES: DrugRouteResponse[] = [
  { code: "PO", labelVi: "Uống" },
  { code: "IV", labelVi: "Tiêm tĩnh mạch" },
  { code: "IM", labelVi: "Tiêm bắp" },
  { code: "SC", labelVi: "Tiêm dưới da" },
  { code: "TOP", labelVi: "Bôi ngoài da" },
  { code: "INH", labelVi: "Khí dung / hít" },
];

// [internalCode, name, activeIngredient, strength, unit, classification,
//  categoryId, manufacturer, sellPriceVnd, nationalDrugCode]
const SEED: [
  string,
  string,
  string,
  string,
  string,
  MedicineClassification,
  string,
  string,
  number,
  string
][] = [
  ["MED-0001", "Amoxicillin 500mg", "Amoxicillin", "500mg", "Viên", "DRUG", "cat-khang-sinh", "Mekophar", 2500, "VN-12345-11"],
  ["MED-0002", "Augmentin 625mg", "Amoxicillin + Acid clavulanic", "625mg", "Viên", "DRUG", "cat-khang-sinh", "GSK", 12000, "VN-22871-15"],
  ["MED-0003", "Paracetamol 500mg", "Paracetamol", "500mg", "Viên", "DRUG", "cat-giam-dau", "DHG Pharma", 800, "VN-18000-13"],
  ["MED-0004", "Efferalgan 500mg", "Paracetamol", "500mg", "Viên sủi", "DRUG", "cat-giam-dau", "Bristol-Myers", 3500, "VN-19211-15"],
  ["MED-0005", "Ibuprofen 400mg", "Ibuprofen", "400mg", "Viên", "DRUG", "cat-giam-dau", "Stella", 1200, "VN-15522-12"],
  ["MED-0006", "Omeprazole 20mg", "Omeprazole", "20mg", "Viên", "DRUG", "cat-tieu-hoa", "Domesco", 1500, "VN-16700-13"],
  ["MED-0007", "Smecta 3g", "Diosmectite", "3g", "Gói", "DRUG", "cat-tieu-hoa", "Ipsen", 4500, "VN-20100-16"],
  ["MED-0008", "Salbutamol 4mg", "Salbutamol", "4mg", "Viên", "DRUG", "cat-ho-hap", "Stella", 700, "VN-13900-11"],
  ["MED-0009", "Ventolin xịt 100mcg", "Salbutamol", "100mcg/liều", "Bình xịt", "DRUG", "cat-ho-hap", "GSK", 85000, "VN-22000-15"],
  ["MED-0010", "Amlodipine 5mg", "Amlodipine", "5mg", "Viên", "DRUG", "cat-tim-mach", "Pymepharco", 1100, "VN-17400-13"],
  ["MED-0011", "Losartan 50mg", "Losartan", "50mg", "Viên", "DRUG", "cat-tim-mach", "Stada", 1800, "VN-18800-14"],
  ["MED-0012", "Vitamin C 500mg", "Acid ascorbic", "500mg", "Viên", "SUPPLEMENT", "cat-vitamin", "Mekophar", 500, "VN-10100-10"],
  ["MED-0013", "Calcium D 600mg", "Calci + Vitamin D3", "600mg", "Viên", "SUPPLEMENT", "cat-vitamin", "DHG Pharma", 1900, "VN-21000-16"],
  ["MED-0014", "Băng gạc vô trùng", "—", "10x10cm", "Miếng", "SUPPLY", "cat-vitamin", "Bảo Thạch", 3000, ""],
  ["MED-0015", "Cồn 70 độ 500ml", "Ethanol", "70%", "Chai", "OTHER", "cat-vitamin", "OPC", 15000, ""],
];

function catName(categoryId: string | null): string | null {
  return CATEGORIES.find((c) => c.id === categoryId)?.name ?? null;
}

function buildMedicines(): MedicineResponse[] {
  return SEED.map(
    (
      [code, name, ai, strength, unit, cls, catId, mfr, price, natCode],
      i
    ) => ({
      id: `med-${String(i + 1).padStart(4, "0")}`,
      internalCode: code,
      name,
      activeIngredient: ai === "—" ? null : ai,
      dosageForm: unit,
      strength,
      unit,
      classification: cls,
      categoryId: catId,
      categoryName: catName(catId),
      manufacturer: mfr,
      routeOfAdministration: cls === "DRUG" ? "PO" : null,
      routeOfAdministrationLabel: cls === "DRUG" ? "Uống" : null,
      mealTiming: cls === "DRUG" ? "AFTER_MEAL" : null,
      dosageInstructions: cls === "DRUG" ? "1 viên x 2 lần/ngày sau ăn" : null,
      sellPriceVnd: price,
      costPriceVnd: Math.round(price * 0.7),
      vatCategory: "VAT_5",
      keywords: `${name} ${ai}`.toLowerCase(),
      imageUrl: null,
      controlled: false,
      active: true,
      visible: i !== 13, // one hidden example
      createdAt: nowISO,
      updatedAt: new Date(Date.now() - i * 3_600_000).toISOString(),
      version: 1,
      codeMappings: natCode
        ? [{ sourceSystem: "DUOC_QUOC_GIA", sourceCode: natCode }]
        : [],
    })
  );
}

const MEDICINES: MedicineResponse[] = buildMedicines();
const NATIONAL_CODE = new Map(
  SEED.filter(([, , , , , , , , , n]) => n).map(
    ([, name, ai, strength, unit, , , mfr, , natCode]) =>
      [
        natCode,
        {
          nationalCode: natCode,
          name,
          activeIngredient: ai === "—" ? null : ai,
          dosageForm: unit,
          strength,
          unit,
          manufacturer: mfr,
          route: "Uống",
          barcode: `893${natCode.replace(/\D/g, "").slice(0, 9).padEnd(9, "0")}`,
          source: "DUOC_QUOC_GIA",
        } satisfies DrugDbItem,
      ] as const
  )
);

function toSummary(m: MedicineResponse): MedicineSummaryResponse {
  return {
    id: m.id,
    internalCode: m.internalCode,
    name: m.name,
    activeIngredient: m.activeIngredient,
    strength: m.strength,
    unit: m.unit,
    classification: m.classification,
    categoryId: m.categoryId,
    categoryName: m.categoryName,
    manufacturer: m.manufacturer,
    sellPriceVnd: m.sellPriceVnd,
    visible: m.visible,
    active: m.active,
    nationalDrugCode:
      m.codeMappings.find((c) => c.sourceSystem === "DUOC_QUOC_GIA")
        ?.sourceCode ?? null,
    barcode: null,
    updatedAt: m.updatedAt,
  };
}

function find(id: string): MedicineResponse {
  const m = MEDICINES.find((x) => x.id === id);
  if (!m) throw new Error(`Mock: thuốc ${id} không tồn tại`);
  return m;
}

function paginate<T>(rows: T[], page = 0, size = 20): PageResponse<T> {
  const totalElements = rows.length;
  return {
    content: rows.slice(page * size, page * size + size),
    totalElements,
    totalPages: Math.max(1, Math.ceil(totalElements / size)),
    number: page,
    size,
  };
}

function fromCreate(
  req: MedicineCreateRequest,
  id: string,
  internalCode: string
): MedicineResponse {
  return {
    id,
    internalCode,
    name: req.name,
    activeIngredient: req.activeIngredient ?? null,
    dosageForm: req.dosageForm ?? null,
    strength: req.strength ?? null,
    unit: req.unit,
    classification: req.classification,
    categoryId: req.categoryId ?? null,
    categoryName: catName(req.categoryId ?? null),
    manufacturer: req.manufacturer ?? null,
    routeOfAdministration: req.routeOfAdministration ?? null,
    routeOfAdministrationLabel:
      ROUTES.find((r) => r.code === req.routeOfAdministration)?.labelVi ?? null,
    mealTiming: req.mealTiming ?? null,
    dosageInstructions: req.dosageInstructions ?? null,
    sellPriceVnd: req.sellPriceVnd ?? 0,
    costPriceVnd: req.costPriceVnd ?? 0,
    vatCategory: req.vatCategory ?? "VAT_5",
    keywords: req.keywords ?? null,
    imageUrl: req.imageUrl ?? null,
    controlled: req.controlled ?? false,
    active: true,
    visible: req.visible ?? true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    version: 1,
    codeMappings: req.nationalDrugCode
      ? [{ sourceSystem: "DUOC_QUOC_GIA", sourceCode: req.nationalDrugCode }]
      : [],
  };
}

/* ------------------------------------------------------------------ */
/* Adapters                                                           */
/* ------------------------------------------------------------------ */

export const mockMedicines = {
  list(
    filter: MedicineListFilter | undefined,
    pageable: { page?: number; size?: number; sort?: string }
  ): Promise<PageResponse<MedicineSummaryResponse>> {
    let rows = MEDICINES;
    if (filter?.q) {
      const n = filter.q.trim().toLowerCase();
      rows = rows.filter(
        (m) =>
          m.name.toLowerCase().includes(n) ||
          m.internalCode.toLowerCase().includes(n) ||
          (m.activeIngredient ?? "").toLowerCase().includes(n)
      );
    }
    if (filter?.classification)
      rows = rows.filter((m) => m.classification === filter.classification);
    if (filter?.categoryId)
      rows = rows.filter((m) => m.categoryId === filter.categoryId);
    if (filter?.visibleOnly) rows = rows.filter((m) => m.visible);
    const sorted = rows
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, "vi"))
      .map(toSummary);
    return delay(paginate(sorted, pageable.page ?? 0, pageable.size ?? 20));
  },

  get(id: string): Promise<MedicineResponse> {
    return delay({ ...find(id) });
  },

  create(req: MedicineCreateRequest): Promise<MedicineResponse> {
    const m = fromCreate(
      req,
      `med-${Math.random().toString(36).slice(2, 8)}`,
      req.internalCode
    );
    MEDICINES.unshift(m);
    return delay({ ...m });
  },

  update(id: string, req: MedicineUpdateRequest): Promise<MedicineResponse> {
    const m = find(id);
    Object.assign(m, {
      name: req.name ?? m.name,
      activeIngredient: req.activeIngredient ?? m.activeIngredient,
      dosageForm: req.dosageForm ?? m.dosageForm,
      strength: req.strength ?? m.strength,
      unit: req.unit ?? m.unit,
      classification: req.classification ?? m.classification,
      categoryId: req.categoryId ?? m.categoryId,
      categoryName: catName(req.categoryId ?? m.categoryId),
      manufacturer: req.manufacturer ?? m.manufacturer,
      sellPriceVnd: req.sellPriceVnd ?? m.sellPriceVnd,
      costPriceVnd: req.costPriceVnd ?? m.costPriceVnd,
      updatedAt: new Date().toISOString(),
      version: m.version + 1,
    });
    return delay({ ...m });
  },

  show(id: string): Promise<void> {
    find(id).visible = true;
    return delay(undefined);
  },
  hide(id: string): Promise<void> {
    find(id).visible = false;
    return delay(undefined);
  },
  deactivate(id: string): Promise<void> {
    find(id).active = false;
    return delay(undefined);
  },
  reactivate(id: string): Promise<void> {
    find(id).active = true;
    return delay(undefined);
  },
  softDelete(id: string): Promise<void> {
    const i = MEDICINES.findIndex((x) => x.id === id);
    if (i >= 0) MEDICINES.splice(i, 1);
    return delay(undefined);
  },
  remove(id: string): Promise<void> {
    const i = MEDICINES.findIndex((x) => x.id === id);
    if (i >= 0) MEDICINES.splice(i, 1);
    return delay(undefined);
  },
  restore(_id: string): Promise<void> {
    return delay(undefined);
  },

  importFromDrugDb(req: ImportFromDrugDbRequest): Promise<MedicineResponse> {
    const src = NATIONAL_CODE.get(req.nationalCode);
    const m = fromCreate(
      {
        internalCode: req.internalCode,
        name: src?.name ?? `Thuốc ${req.nationalCode}`,
        activeIngredient: src?.activeIngredient ?? null,
        dosageForm: src?.dosageForm ?? null,
        strength: src?.strength ?? null,
        unit: src?.unit ?? "Viên",
        classification: req.classification ?? "DRUG",
        categoryId: req.categoryId ?? null,
        manufacturer: src?.manufacturer ?? null,
        sellPriceVnd: req.sellPriceVnd ?? 0,
        nationalDrugCode: req.nationalCode,
      },
      `med-${Math.random().toString(36).slice(2, 8)}`,
      req.internalCode
    );
    MEDICINES.unshift(m);
    return delay({ ...m });
  },

  listCategories(): Promise<MedicineCategoryResponse[]> {
    return delay(CATEGORIES.slice());
  },
  createCategory(req: {
    code: string;
    name: string;
    sortOrder?: number;
  }): Promise<MedicineCategoryResponse> {
    const c: MedicineCategoryResponse = {
      id: `cat-${Math.random().toString(36).slice(2, 8)}`,
      code: req.code,
      name: req.name,
      sortOrder: req.sortOrder ?? CATEGORIES.length + 1,
      isSystem: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    CATEGORIES.push(c);
    return delay(c);
  },

  listRoutes(): Promise<DrugRouteResponse[]> {
    return delay(ROUTES.slice());
  },
};

export const mockDrugDb = {
  search(q: string, limit = 20): Promise<DrugDbItem[]> {
    const n = q.trim().toLowerCase();
    const rows = [...NATIONAL_CODE.values()].filter(
      (d) =>
        d.name.toLowerCase().includes(n) ||
        (d.activeIngredient ?? "").toLowerCase().includes(n) ||
        d.nationalCode.toLowerCase().includes(n)
    );
    return delay(rows.slice(0, limit));
  },
  byCode(nationalCode: string): Promise<DrugDbItem> {
    const d = NATIONAL_CODE.get(nationalCode);
    if (!d) throw new Error(`Mock: mã ${nationalCode} không có trong dược quốc gia`);
    return delay(d);
  },
  byBarcode(barcode: string): Promise<DrugDbItem> {
    const d = [...NATIONAL_CODE.values()].find((x) => x.barcode === barcode);
    if (!d) throw new Error(`Mock: barcode ${barcode} không tìm thấy`);
    return delay(d);
  },
};
